import { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import ReactTooltip from 'react-tooltip'
import colors from '../../../assets/styles/globals.scss'
import moment from 'moment'

import { DATETIME_FORMAT, getMomentInCommunityTimezoneReversed } from '../../../utility/datesHelper'
import { yardageByLevel } from '../../../utility/curriculumHelper'

import { ReactComponent as AccountIcon } from '../../../assets/images/common/icons/sidebarMenu/Account.svg'
import { ReactComponent as AccountLetterIcon } from '../../../assets/images/common/icons/account-letter-icon.svg'
import { ReactComponent as AccountFlagIcon } from '../../../assets/images/common/icons/account-flag-icon.svg'
import { ReactComponent as GolfJournalIcon } from '../../../assets/images/common/icons/golf-journal-icon.svg'
import { ReactComponent as CalendarIcon } from '../../../assets/images/common/icons/calendar-icon-v2.svg'

import classes from './ReportsPage.module.scss'
import classNames from 'classnames'
import DownloadButton from './Reports/DownloadButton'
import PreviewModal from './Reports/PreviewModal'

const CSV_HEADERS = [
  { label: 'Time', key: 'time' },
  { label: 'Pairing Group', key: 'pairingGroup' },
  { label: 'Hole', key: 'hole' },
  { label: 'First Name', key: 'firstName' },
  { label: 'Last Name', key: 'lastName' },
  { label: 'Level', key: 'level' },
  { label: 'Hole Distance', key: 'holeDistance' },
  { label: 'Total Yardage', key: 'totalYardage' },
  { label: 'Player Notes', key: 'playerNotes' },
]

const generateCSVData = (pairings, timezone, type) => {
  const csvData = pairings.pairingGroups.flatMap((group) => (
    group.users?.map((student) => ({
      time: getMomentInCommunityTimezoneReversed(moment(group.startTime), timezone, DATETIME_FORMAT).format('LT'),
      pairingGroup: group.order,
      hole: pairings.format === 'Shotgun'
        ? `${group.startHole?.value || ''} ${group.playersGroup?.value || ''}` : '',
      firstName: student.firstName,
      lastName: student.lastName,
      level: student.level,
      holeDistance: yardageByLevel[student.level] / 9,
      totalYardage: yardageByLevel[student.level],
      playerNotes: student.enrollment.note,
    })) || []
  ))

  if (type === 'PairingsAlphaTeeSheet') {
    csvData.sort((a, b) =>
      a.lastName?.toLowerCase().localeCompare(b.lastName?.toLowerCase())
    )
  }

  return csvData
}

const ReportsPage = ({ pairingsCreated }) => {
  const timezone = useSelector(state => state.user.current_user.communities[0].timezone)
  const event = useSelector(state => state.nineHoleEvent)

  const [selectedReport, setSelectedReport] = useState(null)
  const [printOptions, setPrintOptions] = useState({})
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [data, setData] = useState([])

  const handleDownloadReport = (type) => {
    setSelectedReport(type)
    setData(generateCSVData(event.pairings, timezone, type))
  }

  const downloadPDF = (type, printOptions) => {
    setSelectedReport(type)
    setPrintOptions(printOptions)
    setShowPreviewModal(true)
  }

  const refs = useRef({})
  const handleMouseEnter = id => {
    if (refs.current[id]) {
      ReactTooltip.show(refs.current[id])
    }
  }

  const handleMouseLeave = id => {
    if (refs.current[id]) {
      ReactTooltip.hide(refs.current[id])
    }
  }

  return (
    <div className={classes.eventReportsPage}>
      <ReactTooltip
        type='light'
        effect='solid'
        arrowColor={colors.placeholderGray}
        className={classNames(classes.tooltip)}
        offset={{ top: -5 }}
      />
      <div className='w-75 mx-auto'>
        <div className={classes.reportWrapper}
          onMouseEnter={() => handleMouseEnter(1)}
          onMouseLeave={() => handleMouseLeave(1)}
        >
          <div className={classes.reportInfo}>
            <AccountIcon className={classes.reportIcon}/>
            <div>
              <p className={classes.reportTitle}>Pairings Tee Sheet</p>
              <p className={classes.reportDescription}>
                All golfer pairings organized by tee time and group. Perfect for sharing with staff,
                players, or posting at the course before the round begins.
              </p>
            </div>
          </div>
          <div
            className={classes.downloadButtonWrapper}
            ref={el => (refs.current[1] = el)}
            data-tip={!pairingsCreated ? 'Report available after pairings are created.' : null}
          >
            <DownloadButton
              multiaction
              disabled={!pairingsCreated}
              downloadPDF={() => downloadPDF('PairingsTeeSheet')}
              onClick={() => handleDownloadReport('PairingsTeeSheet')}
              csv={{ headers: CSV_HEADERS, data: data }}
              type='PairingsTeeSheet'
              eventName={event.name}
            />
          </div>
        </div>
        <div className={classes.reportWrapper}
          onMouseEnter={() => handleMouseEnter(2)}
          onMouseLeave={() => handleMouseLeave(2)}
        >
          <div className={classes.reportInfo}>
            <AccountLetterIcon className={classes.reportIcon}/>
            <div>
              <p className={classes.reportTitle}>Alpha Sheet</p>
              <p className={classes.reportDescription}>
                An alphabetical list of all players with their tee times and group assignments.
                Ideal for quickly looking up where and when each player is starting.
              </p>
            </div>
          </div>
          <div
            className={classes.downloadButtonWrapper}
            ref={el => (refs.current[2] = el)}
            data-tip={!pairingsCreated ? 'Report available after pairings are created.' : null}
          >
            <DownloadButton
              multiaction
              disabled={!pairingsCreated}
              downloadPDF={() => downloadPDF('PairingsAlphaTeeSheet')}
              onClick={() => handleDownloadReport('PairingsAlphaTeeSheet')}
              csv={{ headers: CSV_HEADERS, data: data }}
              type='PairingsAlphaTeeSheet'
              eventName={event.name}
            />
          </div>
        </div>
        <div className={classes.reportWrapper}
          onMouseEnter={() => handleMouseEnter(3)}
          onMouseLeave={() => handleMouseLeave(3)}
        >
          <div className={classes.reportInfo}>
            <CalendarIcon className={classes.reportIcon}/>
            <div>
              <p className={classes.reportTitle}>Cart Signs</p>
              <p className={classes.reportDescription}>
                A printable sign displaying each group’s players and starting hole.
                Designed to be placed on golf carts so players can quickly find their assigned cart and group.
              </p>
            </div>
          </div>
          <div
            className={classes.downloadButtonWrapper}
            ref={el => (refs.current[3] = el)}
            data-tip={!pairingsCreated ? 'Report available after pairings are created.' : null}
          >
            <DownloadButton
              disabled={!pairingsCreated}
              onClick={() => downloadPDF('CartSigns')}
              type='CartSigns'
            />
          </div>
        </div>
        <div className={classes.reportWrapper}
          onMouseEnter={() => handleMouseEnter(4)}
          onMouseLeave={() => handleMouseLeave(4)}
        >
          <div className={classes.reportInfo}>
            <GolfJournalIcon className={classes.reportIcon}/>
            <div>
              <p className={classes.reportTitle}>Scorecards</p>
              <p className={classes.reportDescription}>
                Custom Op 36 scorecards for 9-hole events, with player names, yardages, starting tees, and space to track scores from Level 1 and up.
              </p>
            </div>
          </div>
          <div
            className={classes.downloadButtonWrapper}
            ref={el => (refs.current[4] = el)}
            data-tip={!pairingsCreated ? 'Report available after pairings are created.' : null}
          >
            <DownloadButton
              disabled={!pairingsCreated}
              downloadPDF={(printOptions) => downloadPDF('ScoreCards', printOptions)}
              onClick={() => handleDownloadReport('ScoreCards')}
              type='ScoreCards'
            />
          </div>
        </div>
        <div className={classes.reportWrapper}
          onMouseEnter={() => handleMouseEnter(5)}
          onMouseLeave={() => handleMouseLeave(5)}
        >
          <div className={classes.reportInfo}>
            <AccountFlagIcon className={classes.reportIcon}/>
            <div>
              <p className={classes.reportTitle}>Scoreboards</p>
              <p className={classes.reportDescription}>
                A visual scoreboard for showcasing player scores and achievements at your 9-hole events.
                Great for creating a memorable atmosphere and highlighting progress for students, families, and spectators.
              </p>
            </div>
          </div>
          <div
            className={classes.downloadButtonWrapper}
            ref={el => (refs.current[4] = el)}
          >
            <DownloadButton
              onClick={() => downloadPDF('ScoreBoards', { orientation: 'l' })}
              type='ScoreBoards'
            />
          </div>
        </div>
      </div>
      <PreviewModal
        showModal={showPreviewModal === true}
        closeModal={() => setShowPreviewModal(false)}
        report={selectedReport}
        printOptions={printOptions}
        event={event}
      />

    </div>
  )
}

ReportsPage.propTypes = {
  completed: PropTypes.bool,
  pairingsCreated: PropTypes.bool,
}

export default ReportsPage
